/* @flow */
/* eslint-env es6,browser */

//set global variable needed
var __IndexRequestData = window._IndexRequestData || {};
var _cygnus_index_args = window.cygnus_index_args || {};
var _cygnus_index_start: () => string = window.cygnus_index_start || (() => '');
window._IndexRequestData = __IndexRequestData;
window.cygnus_index_args = _cygnus_index_args;
window.cygnus_index_start = _cygnus_index_start;

import * as u from '../utils';
/**
 * @description Implements functionality for index (casalemedia) header bidding
 * @exports bidders/indexbid
 * @requires {@link module:utils}
 * @requires {@link module:jsonp}
 */
class _indexbid {
  _name: string;
  _bInited: 1 | 0 | -1;
  _DefConfig: indexConfig;
  _config: indexConfig;
  _excludeList: Array<string>;

  //Member variable default initialization in constructor
  constructor() {
    this._name = 'index';
    this._bInited = u.F_NOT_INITED;
    this._DefConfig = u.extend(true, this._DefConfig, {
      positions : [],
      exclude : '',
      timeout : 2000,
      qpKey : 'IEXC_BID', //not used only per position qpKey is used
      cVar : 'IEXC_VAR',
      id : 0,
      posTokenMap : {}
    });

    /**
     * @global
     * @typedef {Object} indexConfig
     * @property {Array<string>} positions
     * @property {string} [exclude]
     * @property {number} [timeout=2000]
     * @property {string} [cVar=IEXC_VAR]
     * @property {Object} posTokenMap
     * @property {number} id
     */
    this._config = u.extend(true, this._DefConfig);
    this._excludeList = [];
  }

  /**
   * get list of bid positions supported by this header bidder
   * @param {string} pName
   * @return {Array<string>}
   */
  getBidPositions(pName: string) {
    var ret = this._config.positions;
    if (u.isBrowserExcluded(pName, this._excludeList)) {
      ret = [];
    }
    return ret;
  }

  /**
   * Reset & Update config specific to this bidder
   * @param {indexConfig} oConfig
   */
  updateConfig(oConfig: indexConfig) {
    this._config = u.extend(true, {}, this._DefConfig, oConfig);
    //compute exclusion list again
    this._excludeList = u.getBrowserExcludeList(this._config.exclude);
  }

  /**
   * getter for qpkey
   * @param {string} pos
   */
  getQPKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].qpKey : '';
  }

  /**
   * getter for posKey
   * @param {string} pos
   */
  getPosKey(pos: string) {
    return u.isObject(this._config.posTokenMap[pos]) ? this._config.posTokenMap[pos].id : '';
  }

  /**
   * getter for cVar
   * @param {string} pos
   */
  getCVar() {
    return this._config.cVar;
  }

  /**
   * set the appropriate configs for index partner
   * @access private
   */
  _init(/**posList */) {
    //returns promise
    return new Promise((resolve, reject) => {
      if (this._bInited === u.F_INITED) {
        resolve();
        return;
      }

      //Let's do a basic santiy check (id shdn't be empty etc.,)
      //id should be a number (default set to zero) - index javascript checks for id being number
      if (!u.isNumber(this._config.id) || this._config.id === 0 || !u.isObject(this._config.posTokenMap)) {
        u.logError(`${this._name} config missing mandatory data`);
        reject();
        return;
      }

      var misPos = this._config.positions.filter(pos => !this._config.posTokenMap[pos]);
      if (misPos.length) {
        u.logError(`${this._name} config positions missing in posTokenMap ${misPos.join(',')}`);
        reject();
        return;
      }

      // index js expect height, weight & siteID as number
      let props = ['height', 'width', 'siteID'];
      Object.keys(this._config.posTokenMap)
            .every(pos => props.every(prop => {
              if (!u.isNumber(this._config.posTokenMap[pos][prop])) {
                u.logError(`${this._name} config posTokenMap for ${pos} for property ${prop} is not number`);
                reject();
                return false;
              }
              return true;
           }));

      //configure index global params
      u.extend(true, _cygnus_index_args, {
        timeout : this._config.timeout,
        siteID : this._config.id
      });

      this._bInited = u.F_INITED;
      resolve();
    });
  }


  /**
   * index bids will be stored in index_slots global array
   * Format: IOM_<id>_price
   * Sample: ["IOM_46218_44", "IOM_46219_45", "IOM_46216_73", "IOM_46217_48", "IOM_46220_48"]
   * @access private
   **/
  _processBidResponse(posList: Array<string>, ret: hbPartnerData) {
    if (!u.isFunction(window.index_render)) {
      u.logError(`${this._name} index_render func is not defined`);
      return ret;
    }
    if (!u.isArray(window.index_slots)) {
      u.logError(`${this._name} index_slots response array is not defined`);
      return ret;
    }
    var res = {}, index_params = [], id = '', price = '', slotMap = {};
    window.index_slots.forEach(slot => {
      index_params = slot.split('_');
      if (index_params.length !== 3) {
        return;
      }
      id = index_params[1], price = index_params[2];
      slotMap[id] = {
        price : parseInt(price, 10),
        slot : `${id}_${price}`
      };
    });
    posList.forEach(pos => {
      id = this.getPosKey(pos),
      res = slotMap[id];
      if (res && res.price && res.slot) {
        u.logDebug(`${this._name} id:${id} res.slot:${res.slot} price:${res.price}`);
        ret[pos].info = `${id}|${u.centsToDollar(res.price)}`;

        //query param string to pass to OpenX ad call
        ret[pos]._QP[this.getQPKey(pos)] = u.centsToDollar(res.price);
        ret[pos]._CV[this._config.cVar] = res.slot;
      }
    });
    return ret;
  }

  _getAds(posList: Array<string>, ret: hbPartnerData) {
    return new Promise(resolve => {
      let loadTimer = setTimeout(() => {
        u.logError(`${this._name} Failed to get bids: timeout`);
        resolve(ret);
      }, this._config.timeout);

      // bid response callback
      _cygnus_index_args.callback = () => {
        clearTimeout(loadTimer);
        loadTimer = null;
        resolve(this._processBidResponse(posList, ret));
      };

      const slotIdMap = posList.reduce((o, v) => {
        o[this.getPosKey(v)] = v;
        return o;
      }, {});
      /* eslint-disable arrow-body-style */
      _cygnus_index_args.slots = Object.keys(slotIdMap).map(k => {
        return {
          id : this.getPosKey(slotIdMap[k]),
          width : this._config.posTokenMap[slotIdMap[k]].width,
          height : this._config.posTokenMap[slotIdMap[k]].height,
          siteID : this._config.posTokenMap[slotIdMap[k]].siteID
        };
      });
      /* eslint-enable arrow-body-style */
      // reset internal index structure
      __IndexRequestData.impIDToSlotID = {};
      __IndexRequestData.reqOptions = {};
      var url: string = _cygnus_index_start();
      u.loadJS(url, document, true);
    });
  }

  /**
   * get bids for current partner
   * @param {Array<string>} posList - list of ad positions
   * @param {Object.<string, hbPosData>} ret - header bidding default response
   * @return {Promise<Object.<string, hbPosData>>}
   */
  getBids(posList: Array<string>, ret: hbPartnerData) {
    return this._init(posList).then(() => {
      //get bids
      u.logDebug(`${this._name} Init is done. getting ad bids`);
      return this._getAds(posList, ret);
    }, () => {
      u.logError(`${this._name} init failed`);
      return ret;
    });
  }

 /**
  * render ad for this partner
  * @param {window} win - window object
  * @param {window.document} doc - document object
  * @param {string} pos - ad position
  * @param {string} slot - slot id for the position
  */
  renderAd(win: window, doc: window.document, pos: string, slot: string) {
    try {
      window.index_render(doc, slot);
    } catch(e) {
      u.logDebug(`${this._name} render ad failure ${e.message}`);
    }
  }
}

//export singleton object as default for this module
export default new _indexbid();
